import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	Output,
	SimpleChanges,
	ViewChild
} from '@angular/core';
import { InstagramIntegration } from '@brand-lolly/app/_models/brand-instagram-integration.model';
import {
	LoAvatar,
	LoButton,
	LoStatus,
	LoToggle,
	LoTooltip
} from 'lo-design-system';
import { MetaAccountTypeBadgeComponent } from './meta-account-type-badge.component';
import { MetaWarningPillComponent } from './meta-warning-pill.component';

@Component({
	standalone: true,
	selector: 'meta-ig-account-row',
	imports: [
		LoAvatar,
		LoButton,
		LoStatus,
		LoToggle,
		LoTooltip,
		MetaAccountTypeBadgeComponent,
		MetaWarningPillComponent
	],
	template: `
		<div class="ig-acc">
			<lo-avatar
				[name]="account.ig_username"
				[src]="account.profile_picture_url || ''"
				platform="instagram"
			></lo-avatar>
			<div class="main">
				<span class="lo-type-text-md-semibold">
					&#64;{{ account.ig_username }}
				</span>
				<div class="sub lo-type-text-xs">
					<meta-account-type-badge
						[type]="account.account_type"
					></meta-account-type-badge>
					<span class="muted">{{ sourceLabel }}</span>
					@for (warn of account.top_warnings; track warn.code) {
					<meta-warning-pill [warning]="warn"></meta-warning-pill>
					}
				</div>
			</div>
			@if (statusTone) {
			<lo-status dot [tone]="statusTone">{{ statusLabel }}</lo-status>
			}
			<div
				class="toggle"
				[loTooltip]="toggleTooltip"
				[loTooltipDisabled]="!toggleTooltip"
			>
				<lo-toggle
					[ariaLabel]="'Track @' + account.ig_username"
					[value]="account.is_consumed"
					[disabled]="!canToggle || toggleLoading"
					(valueChange)="onToggle()"
				></lo-toggle>
			</div>
			@if (account.id !== null) {
			<button
				lo-button
				variant="link"
				size="sm"
				type="button"
				(click)="manage.emit(account)"
			>
				{{ isExpired ? 'Reconnect' : 'Manage' }}
			</button>
			}
		</div>
	`,
	styles: [
		`
			:host {
				display: block;

				.ig-acc {
					display: flex;
					align-items: center;
					gap: var(--lo-space-gap-md);
					padding-top: var(--lo-space-gap-md);
					border-top: 1px solid var(--lo-color-border-default);

					.main {
						display: flex;
						flex: 1;
						flex-direction: column;
						gap: var(--lo-space-gap-xs);
						min-width: 0;

						.sub {
							display: flex;
							align-items: center;
							flex-wrap: wrap;
							gap: var(--lo-space-gap-sm);

							.muted {
								color: var(--lo-color-text-secondary);
							}
						}
					}
				}
			}

			:host(:first-of-type) .ig-acc {
				padding-top: 0;
				border-top: none;
			}
		`
	]
})
export class MetaIgAccountRowComponent implements OnChanges {
	@Input({ required: true }) account!: InstagramIntegration;
	@Input() remainingSlots: number | null = null;
	@Input() toggleLoading = false;

	@Output() toggle = new EventEmitter<InstagramIntegration>();
	@ViewChild(LoToggle) private toggleControl?: LoToggle;

	@Output() manage = new EventEmitter<InstagramIntegration>();

	get isExpired(): boolean {
		return (
			this.account.health === 'expired' ||
			this.account.health === 'revoked'
		);
	}

	get statusTone(): 'success' | 'warning' | 'danger' | null {
		if (this.isExpired) return 'danger';
		if (this.account.health === 'expiring') return 'warning';
		if (this.account.health === 'ok' || this.account.is_consumed)
			return 'success';
		return null;
	}

	get statusLabel(): string {
		if (this.account.health === 'revoked') return 'Revoked';
		if (this.isExpired) return 'Expired';
		if (this.account.health === 'expiring') return 'Expiring';
		return 'Healthy';
	}

	get canToggle(): boolean {
		if (this.account.is_consumed) return true;
		if (this.remainingSlots === null) return true;
		return this.remainingSlots > 0;
	}

	get toggleTooltip(): string {
		if (this.canToggle || this.account.is_consumed) return '';
		return 'Plan limit reached — disable another Instagram account first';
	}

	get sourceLabel(): string {
		const sources = this.account.connection_sources ?? [];
		const hasFb = sources.includes('facebook_oauth');
		const hasIg = sources.includes('instagram_oauth');
		const hasPwd = sources.includes('password');
		if (hasFb && hasIg && hasPwd)
			return 'Facebook + Instagram OAuth + password & 2FA';
		if (hasFb && hasIg) return 'Facebook + Instagram OAuth';
		if (hasFb && hasPwd) return 'Facebook + password & 2FA';
		if (hasFb) return 'via Facebook';
		if (hasIg && hasPwd) return 'Instagram OAuth + password & 2FA';
		if (hasIg) return 'Instagram OAuth';
		if (hasPwd) return 'Password + 2FA';
		return 'No credentials';
	}

	ngOnChanges(changes: SimpleChanges): void {
		// TRAP: the switch flips itself on a press and the parent only learns
		// the outcome when the request ends, so a refused change (plan limit,
		// a release blocked by live campaigns) would leave it standing on the
		// wrong side. Put it back on what the account really is once the
		// request is over.
		if (changes['toggleLoading'] && !this.toggleLoading) {
			this.toggleControl?.value.set(this.account.is_consumed);
		}
	}

	onToggle(): void {
		if (!this.canToggle || this.toggleLoading) return;
		this.toggle.emit(this.account);
	}
}
